import { Trash } from 'lucide-react'
import {
  ConfirmationDialog,
  type ConfirmationDialogProps,
} from '@/components/patterns/confirmation-dialog'
import type { PathLink } from '@/hooks/usePathHelper'
import { Button } from '../ui/button'
import { toast } from 'sonner'
import { cn, getErrorMessage } from '@/lib/utils'
import { useNavigate } from '@tanstack/react-router'

type DeleteButtonProps<TId> = Omit<
  ConfirmationDialogProps,
  'title' | 'description' | 'variant' | 'trigger' | 'pendingText' | 'onConfirm' | 'isPending'
> & {
  /** Nothing is deleted while the record has no id. */
  id: TId | undefined
  /** e.g. 'Product' */
  model: string
  /** e.g. deleteProductMutation */
  mutation: { isPending: boolean; mutateAsync: (id: TId) => Promise<unknown> }
  /** e.g. pathHelper.products.index() */
  path?: { link: PathLink }
  disabled?: boolean
  onDeleted?: (id: TId) => unknown
  replace?: boolean
}

function DeleteButton<TId>({
  id,
  model = '',
  mutation,
  path,
  className = '',
  disabled,
  onDeleted,
  replace = false,
  ...props
}: DeleteButtonProps<TId>) {
  const navigate = useNavigate()

  if (!model) {
    throw new Error('`model` is a required prop for DeleteButton')
  }

  const isPending = mutation?.isPending
  const onConfirm = async () => {
    try {
      if (id) {
        await mutation.mutateAsync(id)
        toast.success(`${model} deleted successfully`)
        const navigationTarget = path?.link

        if (navigationTarget) {
          await navigate({ ...navigationTarget, ignoreBlocker: true, replace })
        }

        void Promise.resolve()
          .then(() => onDeleted?.(id))
          .catch(() => undefined)
      }
    } catch (error) {
      toast.error(`Failed to delete ${model}`, {
        description: getErrorMessage(error),
      })
    }
  }
  return (
    <div className={cn('w-full flex items-center justify-end', className)}>
      <ConfirmationDialog
        variant="destructive"
        trigger={
          <Button
            size="sm"
            variant="destructive"
            disabled={disabled}
            className="mt-4 ml-auto inline-flex items-center gap-2"
          >
            <Trash className="" /> Delete {model}
          </Button>
        }
        title={`Delete ${model}`}
        description={`Are you sure you want to delete this ${model.toLowerCase()}?`}
        pendingText="Deleting..."
        onConfirm={onConfirm}
        isPending={isPending}
        {...props}
      />
    </div>
  )
}

export default DeleteButton
